import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { AuthHelperCreator, useAuthHelperCreator } from "@org-avp/avp-avengers-ui-shared-auth";
import { selectMenuOptions, selectOverlay, toggleMenuCollapsed, updateOverlay, useAppDispatch, useProjectParam } from "@org-avp/avp-avengers-ui-shared-store";
import { ConfiguredViewModule, getNavigateTo, MenuItem, viewRegistry } from "@org-avp/avp-avengers-ui-shared-utils";
import { homeViewKey } from "@org-avp/avp-avengers-ui-views-home";
import { useCallback, useMemo } from "react";
import { useTranslation, UseTranslationResponse } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

export interface MenuItemsHelper {
  topItems: MenuItem[];
  bottomItems: MenuItem[];
  collapsed: boolean;
  selectedKeys: string[];
}

export function useMenuItemsHelper(): MenuItemsHelper {
  const { t } = useTranslation();
  const appDispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const currentOverlay = useSelector(selectOverlay);
  const fullSizeViews = useMemo(
    () => viewRegistry.getViews({ type: "fullSize" }),
    [],
  );

  const selectedKeys: [string] | [] = useMemo(() => {
    const isViewSelected = (view: ConfiguredViewModule) => `/${getNavigateTo(view)}` === location.pathname;
    const selectedKey = currentOverlay ?? Array.from(fullSizeViews.values()).find(isViewSelected)?.viewKey;
    if (!selectedKey && (location.pathname === "/" || location.pathname === "")) {
      return [homeViewKey];
    }
    return selectedKey ? [selectedKey] : [];
  }, [currentOverlay, fullSizeViews, location.pathname]);

  const showView = useCallback((viewKey: string) => () => {
    const currOverlay = currentOverlay;
    appDispatch(updateOverlay(null));
    if (viewKey !== selectedKeys[0] && viewKey !== currOverlay) {
      const fullSizeView = fullSizeViews.get(viewKey);
      if (fullSizeView != null) {
        if (fullSizeView?.config?.overlay) appDispatch(updateOverlay(viewKey));
        else navigate(getNavigateTo(fullSizeView));
      }
    }
  }, [appDispatch, currentOverlay, fullSizeViews, navigate, selectedKeys]);

  const { collapsed } = useSelector(selectMenuOptions);
  const toggleCollapsed = useCallback(() => {
    appDispatch(toggleMenuCollapsed());
  }, [appDispatch]);

  const brand = useProjectParam("brand");
  const projectId = useProjectParam("projectId");
  const authHelperCreator = useAuthHelperCreator();

  const topItems: MenuItem[] = useMemo(
    () => Array.from(fullSizeViews.values())
      .filter((view) => view.config.menuLocation === "top")
      .flatMap((view) => mapView(view, showView, t, authHelperCreator, brand, projectId)),
    [authHelperCreator, brand, fullSizeViews, projectId, showView, t],
  );

  const bottomItems: MenuItem[] = useMemo(
    () => [
      ...Array.from(fullSizeViews.values())
        .filter((view) => view.config.menuLocation === "bottom")
        .flatMap((view) => mapView(view, showView, t, authHelperCreator, brand, projectId)),
      { type: "divider" },
      {
        key: "toggle",
        icon: collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>,
        label: t(collapsed ? "app:menuBar.expand" : "app:menuBar.collapse"),
        onClick: toggleCollapsed,
      },
    ],
    [authHelperCreator, brand, collapsed, fullSizeViews, projectId, showView, t, toggleCollapsed],
  );

  return useMemo(
    () => ({ topItems, bottomItems, collapsed, selectedKeys }),
    [topItems, bottomItems, collapsed, selectedKeys],
  );
}

function mapView(
  view: ConfiguredViewModule & { config: { type: "fullSize" } },
  showView: (viewKey: string) => () => void,
  t: UseTranslationResponse<"translation">["t"],
  authHelperCreator: AuthHelperCreator,
  brand: string | null | undefined,
  project: string | null | undefined,
): MenuItem[] {
  const items: MenuItem[] = [{
    key: view.viewKey,
    icon: view.menuIconEl,
    onClick: showView(view.viewKey),
    label: view.displayNameI18nKey ? t(view.displayNameI18nKey) : undefined,
    disabled: view.isAuthorized ? !view.isAuthorized(authHelperCreator, { brand, project }) : false,
  }];
  if (view.config.menuDividerBelow) {
    items.push({ type: "divider" });
  }
  return items;
}
